import { useOrganizationList } from '@clerk/clerk-react'
import { trackEvent } from '@saaga/shared/utils/analytics.util'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import type { CreateOrganizationFormData } from '@/components/org-settings/org-settings.interface'
import {
  generateSlugSuggestions,
  getClerkErrors,
  isClerkSlugConflict,
  SlugConflictError,
} from '@/components/org-settings/org-slug.util'

export interface CreateOrganizationDialogState {
  isOpen: boolean
  isLoading: boolean
}

export interface UseCreateOrganizationReturn {
  dialogState: CreateOrganizationDialogState
  handleOpenDialog: () => void
  handleDialogChange: (isOpen: boolean) => void
  handleSubmit: (data: CreateOrganizationFormData) => void
}

const getCreateOrgErrorMessage = (error: unknown): string =>
  getClerkErrors(error)?.[0]?.message ?? 'Failed to create organization. Please try again.'

export const useCreateOrganization = (): UseCreateOrganizationReturn => {
  const navigate = useNavigate()
  const { setActive, createOrganization, userMemberships } = useOrganizationList({
    userMemberships: { infinite: true },
  })

  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const handleOpenDialog = useCallback(() => setIsOpen(true), [])
  const handleDialogChange = useCallback((open: boolean) => setIsOpen(open), [])

  const handleSubmit = useCallback(
    async (data: CreateOrganizationFormData) => {
      if (!createOrganization || !setActive) return

      setIsLoading(true)
      try {
        const newOrg = await createOrganization({ name: data.name, slug: data.slug })

        if (data.logoFile && newOrg) {
          await newOrg.setLogo({ file: data.logoFile })
        }

        if (newOrg) {
          await setActive({ organization: newOrg.id })
          await userMemberships?.revalidate()
          setIsOpen(false)
          trackEvent('organization_created', { organizationName: data.name })
          toast.success('Organization created successfully')
          navigate(`/${newOrg.slug}`)
        }
      } catch (error) {
        if (isClerkSlugConflict(error)) {
          throw new SlugConflictError(data.slug, generateSlugSuggestions(data.slug))
        }
        logger.error('Failed to create organization:', error)
        toast.error(getCreateOrgErrorMessage(error))
      } finally {
        setIsLoading(false)
      }
    },
    [createOrganization, setActive, navigate, userMemberships]
  )

  return {
    dialogState: { isOpen, isLoading },
    handleOpenDialog,
    handleDialogChange,
    handleSubmit,
  }
}
